import { useCallback, useEffect, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, RotateCcw } from 'lucide-react';
import { ConfirmModal, useConfirmModal } from '@footsteps/shared';
import { useWozCase, useWozCaseManager } from '../components/woz-case-manager/woz-case-manager.provider';
import { routes } from '../routes';
import { WozCaseComponent } from '@/components/woz-case-manager/woz-case';

export const Intake = (): JSX.Element => {
  const { caseManagerState } = useWozCaseManager();
  const navigate = useNavigate();

  const isReady = caseManagerState.auth.isResolved && !caseManagerState.cases.isLoading;

  useEffect(() => {
    if (isReady && !caseManagerState.currentCase) {
      navigate(routes.home, { replace: true });
    }
  }, [isReady, caseManagerState.currentCase, navigate]);

  if (!caseManagerState.currentCase) return <></>;

  return <IntakeContent />;
};

const IntakeContent = (): JSX.Element => {
  const { caseState, caseViewModel, chatbotViewModel } = useWozCase();
  const navigate = useNavigate();
  const [confirmState, confirmModal] = useConfirmModal();

  // Navigate to summary when the flow is complete (live or restored)
  useEffect(() => {
    if (caseState.isFlowComplete) {
      navigate(routes.summary);
    }
  }, [caseState.isFlowComplete, navigate]);

  const handleResetClick = useCallback(() => {
    confirmModal.open({
      title: 'Opnieuw beginnen',
      message: 'Weet je zeker dat je het gesprek opnieuw wilt starten? Al je antwoorden worden gewist.',
      confirmLabel: 'Opnieuw beginnen',
      cancelLabel: 'Annuleren',
      onConfirm: () => {
        chatbotViewModel.reset();
        caseViewModel.reset();
        caseViewModel.startFlow();
      },
    });
  }, [confirmModal, chatbotViewModel, caseViewModel]);

  const handlePrint = useCallback(() => window.print(), []);

  return (
    <div className='intake-page'>
      <header className='intake-topbar'>
        <div className='intake-topbar-content'>
          <img src='/images/chatbot-avatar-512.png' alt='WOZ Taxateur' className='intake-topbar-avatar' />
          <div className='intake-topbar-actions'>
            <button
              type='button'
              className='intake-topbar-btn'
              onClick={handlePrint}
              title='Print gesprek'
              aria-label='Print gesprek'
            >
              <Printer size={16} />
            </button>
            <button
              type='button'
              className='intake-topbar-btn'
              onClick={handleResetClick}
              title='Opnieuw beginnen'
              aria-label='Opnieuw beginnen'
            >
              <RotateCcw size={16} />
            </button>
          </div>
        </div>
      </header>
      <main className='intake-body'>
        <WozCaseComponent />
      </main>

      <ConfirmModal state={confirmState} onConfirm={confirmModal.confirm} onCancel={confirmModal.cancel} />
    </div>
  );
};
